import React, { useEffect, useState } from 'react'
import { useFlag } from '@sketch/modules-common'
import {
  CreateAnnotationMutation,
  CreatePreviewAnnotationMutation,
} from '@sketch/gql-types'

import AnnotationCommentBox from '../AnnotationCommentBox'
import AnnotationPopoverWrapper from '../AnnotationPopoverWrapper'

import useCreateAnnotation from '../../operations/useCreateAnnotation'
import { Coordinates, DraftAnnotationParameters } from '../../types'

import { useAnnotationOverlayContext } from '../../hooks'
import { useIsPreviewView } from '../../../shares/views/PreviewView/PreviewContext'

interface DraftAnnotationPopoverProps {
  coordinates: Coordinates
  parameters: DraftAnnotationParameters
  isViewingLatestVersion: boolean
  onSubmit?: (
    data: CreateAnnotationMutation | CreatePreviewAnnotationMutation
  ) => void
}

const DraftAnnotationPopover = (props: DraftAnnotationPopoverProps) => {
  const { coordinates, parameters, onSubmit, isViewingLatestVersion } = props
  const { shareIdentifier, subject: parameterSubject } = parameters
  const isPreview = useIsPreviewView()

  const includeHasUnreadComments = useFlag('pages-unread-annotations')

  const {
    draftAnnotationComment,
    setDraftAnnotationComment,
    setAnnotationInFlight,
  } = useAnnotationOverlayContext() || {}

  const [commentValue, setCommentValue] = useState(draftAnnotationComment || '')

  // Throttle context updates to avoid re-rendering all consumers on every keystroke
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDraftAnnotationComment?.(commentValue)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [commentValue, setDraftAnnotationComment])

  const createAnnotation = useCreateAnnotation({
    onCacheUpdated: onSubmit,
    onComplete: onSubmit,
  })

  return (
    <AnnotationPopoverWrapper>
      <AnnotationCommentBox
        shareIdentifier={shareIdentifier}
        showOlderVersionWarning={!isViewingLatestVersion}
        onCommentChange={setCommentValue}
        comment={draftAnnotationComment || undefined}
        onCommentSubmit={async body => {
          const baseVariables = {
            shareIdentifier,
            body,
            coordinates,
            includeHasUnreadComments,
          }

          setAnnotationInFlight?.(true)

          if (parameterSubject.type === 'ARTBOARD' && !isPreview) {
            const { permanentPageId, ...subject } = parameterSubject
            await createAnnotation(
              { ...baseVariables, subject },
              { permanentPageId }
            )
          } else {
            const subject = parameterSubject
            await createAnnotation({ ...baseVariables, subject })
          }

          setAnnotationInFlight?.(false)
        }}
        autoFocus
      />
    </AnnotationPopoverWrapper>
  )
}

export default DraftAnnotationPopover
